import type { ModuleKey } from '@fos/shared/enums';
import { MODULE_LABELS } from '@fos/shared/labels';
import { Compass, Hammer, Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { useAccess } from '../../../lib/useAccess';
import { Button } from '../../../ui/Button';
import { Page } from '../../../ui/Page';
import { PageHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { EmptyState } from '../../../ui/States';

/** Opisy modułów (jedno zdanie, spokojnie). Strony powstają w kolejnych falach budowy. */
const MODULE_INFO: Record<ModuleKey, { meta: string; soon: string; width?: 'default' | 'narrow' }> = {
  inbox: {
    meta: 'Wiadomości od klientów i zdarzenia z systemu w jednym miejscu.',
    soon: 'Tu pojawią się wiadomości i zdarzenia, z oznaczeniem nieprzeczytanych.',
  },
  approvals: {
    meta: 'Działania AI, które czekają na Twoją zgodę. Bez niej nic się nie wykona.',
    soon: 'Tu zobaczysz maile, faktury i wdrożenia przygotowane przez AI, z podglądem przed zgodą.',
    width: 'narrow',
  },
  tasks: {
    meta: 'Zadania Twoje, zespołu i AI.',
    soon: 'Tu pojawi się lista i tablica zadań z kartą zadania i checklistą.',
  },
  clients: {
    meta: 'Klienci, kontakty, projekty i usługi.',
    soon: 'Tu pojawi się lista klientów i ich profile z ofertą, czasem i zadaniami.',
  },
  time: {
    meta: 'Wpisy czasu od–do, raport i zestawienia PDF.',
    soon: 'Tu wpiszesz czas pracy i przygotujesz raport dla klienta.',
  },
  projects: {
    meta: 'Projekty klientów i wewnętrzne.',
    soon: 'Tu pojawi się lista projektów z budżetem godzin i terminami.',
  },
  maintenance: {
    meta: 'Strony i aplikacje na naszym serwerze, umowy, płatności cykliczne i dostępy.',
    soon: 'Tu pojawią się zasoby, odnowienia i przypomnienia o płatnościach.',
  },
  billing: {
    meta: 'Zestawienia czasu i faktury.',
    soon: 'Tu przygotujesz zestawienie dla klienta i wystawisz fakturę przez Akceptacje.',
  },
  knowledge: {
    meta: 'Notatki, decyzje i pliki klientów i projektów.',
    soon: 'Tu pojawią się notatki w Markdown z historią wersji i decyzjami.',
  },
  agents: {
    meta: 'Przebiegi agentów AI i ich wyniki.',
    soon: 'Tu zobaczysz, co zrobili agenci: zmiany, testy i wyniki do odbioru.',
  },
};

/** Strona modułu poza Fazą 0: nagłówek + spokojny pusty stan „Moduł w budowie”. Bez dostępu: komunikat. */
export function ModulePage({ module, children }: { module: ModuleKey; children?: ReactNode }) {
  const access = useAccess();
  const label = MODULE_LABELS[module];
  const info = MODULE_INFO[module];

  if (!access.can(module)) {
    return (
      <Page>
        <PageHeader title={label} />
        <Section className="mt-6">
          <EmptyState
            icon={Lock}
            title={`Brak dostępu do modułu ${label}`}
            description="Jeśli potrzebujesz dostępu, poproś administratora workspace’u."
            action={
              <Button asChild variant="secondary">
                <Link to="/dzis">Wróć do Dziś</Link>
              </Button>
            }
          />
        </Section>
      </Page>
    );
  }

  return (
    <Page width={info.width}>
      <PageHeader title={label} meta={info.meta} />
      {children ?? (
        <div className="mt-10 rounded-xl border border-dashed border-line">
          <EmptyState icon={Hammer} title="Moduł w budowie" description={info.soon} />
        </div>
      )}
    </Page>
  );
}

/** Profil klienta (/klienci/:id) — powstaje w Fali 1. */
export function ClientDetailPage() {
  const { id } = useParams();
  const access = useAccess();
  if (!access.can('clients')) return <ModulePage module="clients" />;
  return (
    <Page>
      <PageHeader
        breadcrumbs={[{ label: 'Klienci', to: '/klienci' }]}
        title="Klient"
        meta={id ? `ID ${id}` : undefined}
      />
      <div className="mt-10 rounded-xl border border-dashed border-line">
        <EmptyState
          icon={Hammer}
          title="Profil klienta w budowie"
          description="Tu pojawi się oferta z wykorzystaniem godzin, rozliczenia, projekty, usługi i otwarte zadania."
        />
      </div>
    </Page>
  );
}

export function NotFoundPage() {
  return (
    <Page>
      <Section>
        <EmptyState
          icon={Compass}
          title="Nie ma takiej strony"
          description="Adres mógł się zmienić albo strona została przeniesiona."
          action={
            <Button asChild variant="secondary">
              <Link to="/dzis">Wróć do Dziś</Link>
            </Button>
          }
        />
      </Section>
    </Page>
  );
}
